<template>
	<v-dialog
		:model-value="modelValue"
		max-width="1200"
		@update:model-value="close"
	>
		<v-card :style="kindColorVars">
			<v-toolbar color="primary" flat class="drag-handle">
				<v-toolbar-title class="text-body-1">
					Session timeline
				</v-toolbar-title>
				<v-spacer />
				<v-btn icon="mdi-close" variant="text" @click="close" />
			</v-toolbar>

			<v-card-text>
				<div class="text-body-2 mb-2">
					<span class="font-weight-bold">
						{{ paperName || "Untitled paper" }}
					</span>
					<span v-if="userEmail"> - {{ userEmail }}</span>
				</div>

				<div class="d-flex align-center flex-wrap ga-4 mb-2 text-caption">
					<span class="d-flex align-center ga-1">
						<span class="timeline-swatch swatch-active" />
						Active minutes
					</span>
					<v-tooltip
						location="top"
						max-width="320"
						text="Minutes with mouse or keyboard input but no recorded action: could be reading or scrolling within a page, could be idle mouse movement"
					>
						<template #activator="{ props: activator }">
							<span v-bind="activator" class="d-flex align-center ga-1">
								<span class="timeline-swatch swatch-input-only" />
								Input-only minutes
								<v-icon size="x-small" icon="mdi-help-circle-outline" />
							</span>
						</template>
					</v-tooltip>
					<span class="d-flex align-center ga-1">
						<span class="timeline-swatch swatch-span" />
						Session span
					</span>
					<span class="d-flex align-center ga-1">
						<span
							class="timeline-swatch timeline-swatch-ribbon swatch-orientation"
						/>
						Orientation
					</span>
					<span class="d-flex align-center ga-1">
						<span
							class="timeline-swatch timeline-swatch-ribbon swatch-text-work"
						/>
						Text
					</span>
					<span class="d-flex align-center ga-1">
						<span
							class="timeline-swatch timeline-swatch-ribbon swatch-graph-work"
						/>
						Graph
					</span>
					<span class="d-flex align-center ga-1">
						<span
							class="timeline-swatch timeline-swatch-ribbon swatch-table-work"
						/>
						Table
					</span>
				</div>

				<div
					v-if="!loading && !sessions.length"
					class="pa-6 text-disabled text-center"
				>
					{{
						userUuid
							? "No recorded sessions for this user on this SDE."
							: "No recorded sessions for this SDE."
					}}
				</div>
				<div
					v-else
					class="timeline-wrap"
					:class="{ 'timeline-wrap-loading': loading }"
				>
					<div
						v-if="!ready && modelValue"
						class="d-flex justify-center align-center timeline-spinner"
					>
						<v-progress-circular indeterminate color="primary" size="48" />
					</div>
					<div ref="timelineEl" class="timeline-container" />
				</div>

				<div class="text-caption text-medium-emphasis mt-2">
					Drag to pan, scroll to zoom.
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import { DataSet, Timeline } from "vis-timeline/standalone";
import "vis-timeline/styles/vis-timeline-graph2d.css";

import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import { timelineKindColors } from "@/constants";
import pluralize from "@/utils/pluralize";

const MINUTE_MS = 60000;

const WORK_KIND_LABELS = {
	orientation: "orientation",
	text: "text work",
	graph: "graph work",
	table: "table work",
};

// The two fixed lanes. A recorder session is minted per page load, so
// per-session rows turned refresh-happy history into a long scroll; instead
// each surface renders as ONE lane holding the union of its sessions.
const CONTEXT_LANES = [
	{ appContext: "sde_v2", title: "Extraction" },
	{ appContext: "sde_v2_viewer", title: "Raw data viewer" },
];

const BAR_CLASSES = {
	active: "tl-active",
	inputOnly: "tl-input-only",
};

const BAR_TITLES = {
	active: (count) => `${count} active min`,
	inputOnly: (count) => `${count} input-only min`,
};

/**
 * Merge sorted ISO minute-bucket starts into contiguous [startMs, endMs) runs
 * so one v-timeline item covers a stretch of minutes instead of sixty slivers.
 */
function minuteRuns(isoMinutes) {
	const runs = [];
	for (const iso of isoMinutes) {
		const ms = new Date(iso).getTime();
		const last = runs[runs.length - 1];
		if (last && ms === last.endMs) {
			last.endMs = ms + MINUTE_MS;
		} else {
			runs.push({ startMs: ms, endMs: ms + MINUTE_MS });
		}
	}
	return runs;
}

export default {
	name: "TimelineDialog",

	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		dataEntryUuid: {
			type: String,
			default: null,
		},
		userUuid: {
			type: String,
			default: null,
		},
		paperName: {
			type: String,
			default: "",
		},
		userEmail: {
			type: String,
			default: "",
		},
	},

	emits: ["update:modelValue"],

	data() {
		return {
			loading: false,
			// True once vis reports onInitialDrawComplete - the moment it
			// unhides its own root and real pixels exist. The spinner covers
			// everything before that.
			ready: false,
			sessions: [],
			timeline: null,
			// Bumped on every destroy so in-flight retry/settle callbacks from
			// a previous open can recognize they are stale and stop.
			renderGeneration: 0,
		};
	},

	computed: {
		// The kind palette flows into the CSS as custom properties so the
		// ribbon and legend-swatch rules share one source of truth.
		kindColorVars() {
			return Object.fromEntries(
				Object.entries(timelineKindColors).map(([kind, color]) => [
					`--tl-kind-${kind}`,
					color,
				]),
			);
		},
	},

	watch: {
		// Fetch on open rather than on mount: the dialog lives in the page and
		// would otherwise query on every page load.
		modelValue(isOpen) {
			if (isOpen) {
				this.getTimeline();
			} else {
				this.destroyTimeline();
			}
		},
	},

	beforeUnmount() {
		this.destroyTimeline();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		close() {
			this.$emit("update:modelValue", false);
		},

		getTimeline() {
			this.loading = true;
			this.ready = false;
			this.$http
				.get("/activity-events/session-timeline", {
					params: {
						dataEntryUuid: this.dataEntryUuid,
						...(this.userUuid ? { userUuid: this.userUuid } : {}),
					},
				})
				.then((response) => {
					this.sessions = response.data.sessionTimeline.sessions;
					this.$nextTick(() => this.renderTimeline());
				})
				.catch((error) => {
					if (error.response?.data?.errors?.length) {
						this.showNotification({
							message: error.response.data.errors[0].message,
							status: "error",
						});
					} else {
						this.showNotification({
							message: "Could not load the session timeline.",
							status: "error",
						});
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},

		// Built as a DOM node: vis passes string content through its xss
		// filter, which strips class attributes; an element is used verbatim.
		laneLabel(lane) {
			const started = new Date(
				Math.min(...lane.sessions.map((s) => new Date(s.startedAt).getTime())),
			);
			const ended = new Date(
				Math.max(...lane.sessions.map((s) => new Date(s.endedAt).getTime())),
			);
			const time = (date) =>
				date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
			const sameDay = started.toDateString() === ended.toDateString();
			const range = sameDay
				? `${started.toLocaleDateString()} · ${time(started)} - ${time(ended)}`
				: `${started.toLocaleDateString()} - ${ended.toLocaleDateString()}`;
			const count = lane.sessions.length;

			const label = document.createElement("div");
			const title = document.createElement("div");
			title.className = "tl-group-title";
			title.textContent = lane.title;
			const sub = document.createElement("div");
			sub.className = "tl-group-sub";
			sub.textContent = `${pluralize("session", count, true)} · ${range}`;
			label.appendChild(title);
			label.appendChild(sub);
			return label;
		},

		destroyTimeline() {
			this.renderGeneration += 1;
			if (this.timeline) {
				this.timeline.destroy();
				this.timeline = null;
			}
			this.ready = false;
		},

		renderTimeline() {
			this.destroyTimeline();
			if (!this.sessions.length) {
				return;
			}

			// NEVER construct into a missing or zero-width container (a
			// reopening dialog's content can be unmounted mid-transition or
			// still hidden): vis would lay out degenerate and only self-correct
			// on its 1s autoResize poll - the exact "invisible chart for a
			// second" this dialog kept exhibiting. Retry by frame until the
			// container really exists and is sized; the spinner covers the
			// wait, and the generation guard stops stale retries after a close
			// or a newer render.
			if (!this.$refs.timelineEl?.offsetWidth) {
				const generation = this.renderGeneration;
				requestAnimationFrame(() => {
					if (this.modelValue && this.renderGeneration === generation) {
						this.renderTimeline();
					}
				});
				return;
			}

			const lanes = CONTEXT_LANES.map((lane) => ({
				...lane,
				sessions: this.sessions.filter(
					(session) => session.appContext === lane.appContext,
				),
			})).filter((lane) => lane.sessions.length);

			const groups = new DataSet(
				lanes.map((lane, index) => ({
					id: lane.appContext,
					content: this.laneLabel(lane),
					order: index,
				})),
			);

			const items = [];
			let probe = null;
			// Everything is drawn on the minute grid the metrics count in: the
			// minute bars are whole minute buckets (an event at :55 marks its
			// whole minute), so the span backgrounds snap to the same grid or
			// the bars would start before and outsize their sessions.
			const floorMinute = (iso) =>
				Math.floor(new Date(iso).getTime() / MINUTE_MS) * MINUTE_MS;
			const ceilMinute = (iso) =>
				Math.ceil(new Date(iso).getTime() / MINUTE_MS) * MINUTE_MS;
			for (const lane of lanes) {
				const group = lane.appContext;
				// One background span PER SESSION, all in the lane: the span
				// edges are what still show individual session boundaries
				// (page reloads, gaps); overlapping backgrounds in one group
				// overlay harmlessly.
				for (const session of lane.sessions) {
					const spanStartMs = floorMinute(session.startedAt);
					const spanEndMs = Math.max(
						ceilMinute(session.endedAt),
						spanStartMs + MINUTE_MS,
					);
					items.push({
						id: `${group}-span-${session.sessionUuid}`,
						group,
						start: new Date(spanStartMs),
						end: new Date(spanEndMs),
						type: "background",
						className: "tl-span",
					});
				}

				// Minute bars: union each classification across the lane's
				// sessions, then re-impose the split with precedence active >
				// input-only - a minute active in one session and input-only
				// in an overlapping one was worked in, and every minute
				// renders at most once. Idle minutes get NO bar at all: idle
				// rests on visibilityState, which tracks page-on-screen, not
				// attention (a visible-but-unfocused window on a second
				// monitor produces idle minutes all afternoon), so the
				// timeline treats idle as plain session-span background
				// rather than a first-class state.
				const active = new Set();
				const inputOnly = new Set();
				for (const session of lane.sessions) {
					for (const iso of session.activeMinutes) active.add(iso);
					for (const iso of session.inputOnlyMinutes) inputOnly.add(iso);
				}
				for (const iso of active) {
					inputOnly.delete(iso);
				}
				// ISO-8601 strings sort chronologically; minuteRuns needs order.
				const sortedIso = (set) => [...set].sort();
				for (const [kind, runs] of [
					["active", minuteRuns(sortedIso(active))],
					["inputOnly", minuteRuns(sortedIso(inputOnly))],
				]) {
					for (const run of runs) {
						const count = Math.round((run.endMs - run.startMs) / MINUTE_MS);
						let className = BAR_CLASSES[kind];
						// The first run doubles as the paint probe: its
						// rendered width vs its known duration tells whether
						// the DOM actually reflects the fitted window yet.
						if (!probe) {
							probe = { ms: run.endMs - run.startMs };
							className += " tl-probe";
						}
						items.push({
							id: `${group}-${kind}-${run.startMs}`,
							group,
							start: new Date(run.startMs),
							end: new Date(run.endMs),
							type: "range",
							className,
							title: BAR_TITLES[kind](count),
						});
					}
				}

				// Kind ribbons: each ACTIVE minute is classified into a thin
				// ribbon under the solid bar - "orientation" before the
				// boundary, and past it the tab whose window OVERLAPS the
				// minute the most (midpoint containment dropped trailing
				// minutes whose window covered only their first seconds).
				// Orientation and tab windows are per-session concepts, so
				// classification runs per session; when two overlapping
				// sessions classify the same minute differently, the
				// earlier-STARTED session wins (deterministic, and the
				// earlier session is the one whose work occupied the minute
				// first). Sessions that never reach the boundary contribute
				// no ribbons - consistent with the metrics, where a session
				// with no extraction action has no orientation and no work
				// of any kind.
				const ribbonKinds = new Map();
				const bySessionStart = [...lane.sessions].sort((a, b) =>
					a.startedAt.localeCompare(b.startedAt),
				);
				for (const session of bySessionStart) {
					const orientationEndMs = session.orientationEndedAt
						? new Date(session.orientationEndedAt).getTime()
						: null;
					const windows = (session.tabWindows ?? []).map((w) => ({
						tab: w.tab,
						startMs: new Date(w.startedAt).getTime(),
						endMs: new Date(w.endedAt).getTime(),
					}));
					const kindAt = (bucketMs) => {
						if (orientationEndMs === null) {
							return null;
						}
						if (bucketMs + MINUTE_MS <= orientationEndMs) {
							return "orientation";
						}
						let best = null;
						let bestOverlap = 0;
						for (const w of windows) {
							const overlap =
								Math.min(bucketMs + MINUTE_MS, w.endMs) -
								Math.max(bucketMs, w.startMs);
							if (overlap > bestOverlap) {
								bestOverlap = overlap;
								best = w;
							}
						}
						return best && WORK_KIND_LABELS[best.tab] ? best.tab : null;
					};
					for (const isoMinute of session.activeMinutes) {
						const ms = new Date(isoMinute).getTime();
						if (ribbonKinds.has(ms)) {
							continue;
						}
						const kind = kindAt(ms);
						if (kind) {
							ribbonKinds.set(ms, kind);
						}
					}
				}
				// Consecutive same-kind minutes merge into one ribbon, across
				// session boundaries too (the map is minute-keyed).
				let ribbon = null;
				const flushRibbon = () => {
					if (!ribbon) return;
					const count = Math.round((ribbon.endMs - ribbon.startMs) / MINUTE_MS);
					items.push({
						id: `${group}-ribbon-${ribbon.startMs}`,
						group,
						start: new Date(ribbon.startMs),
						end: new Date(ribbon.endMs),
						type: "range",
						className: `tl-ribbon tl-ribbon-${ribbon.kind}`,
						title: `${count} min ${WORK_KIND_LABELS[ribbon.kind]}`,
					});
					ribbon = null;
				};
				for (const ms of [...ribbonKinds.keys()].sort((a, b) => a - b)) {
					const kind = ribbonKinds.get(ms);
					if (ribbon && ribbon.kind === kind && ms === ribbon.endMs) {
						ribbon.endMs = ms + MINUTE_MS;
					} else {
						flushRibbon();
						ribbon = { kind, startMs: ms, endMs: ms + MINUTE_MS };
					}
				}
				flushRibbon();
			}

			const starts = this.sessions.map((session) =>
				new Date(session.startedAt).getTime(),
			);
			const ends = this.sessions.map((session) =>
				new Date(session.endedAt).getTime(),
			);
			const spanStart = Math.min(...starts);
			const spanEnd = Math.max(...ends, spanStart + MINUTE_MS);
			const pad = Math.max((spanEnd - spanStart) * 0.05, MINUTE_MS);

			// The reveal signal is vis's own onInitialDrawComplete - the
			// library keeps its root at inline visibility:hidden until its
			// initial draw really finishes, so this callback is the moment
			// actual pixels appear. Three traps this design dodges, learned
			// the hard way: DOM-geometry polling reveals before vis unhides
			// itself (a blank chart with a healthy DOM); passing start/end as
			// CONSTRUCTOR options makes the unhide wait for a rangechanged
			// round-trip that can land on vis's 1s watch timer; and setting a
			// window MANUALLY after construction competes with vis's own
			// initial fit, so the chart visibly re-zooms a beat after
			// appearing. So: no start/end anywhere - vis's initial fit is the
			// one and only window authority.
			const generation = this.renderGeneration;
			let drawComplete = false;
			this.timeline = new Timeline(
				this.$refs.timelineEl,
				new DataSet(items),
				groups,
				{
					stack: false,
					selectable: false,
					// No red "now" marker: this is a record of past sessions,
					// not a live tracker.
					showCurrentTime: false,
					orientation: "top",
					margin: { item: 6 },
					min: new Date(spanStart - pad * 4),
					max: new Date(spanEnd + pad * 4),
					maxHeight: 460,
					tooltip: { followMouse: true },
					onInitialDrawComplete: () => {
						drawComplete = true;
					},
				},
			);

			// Reveal only when what the user would see is CORRECT: vis has
			// unhidden its root (onInitialDrawComplete) AND the window has
			// been fitted to the data. vis applies its initial fit() on a
			// separate redraw cycle from the unhide, so between the two the
			// chart is visible but zoomed to a days-wide default window; the
			// opaque spinner cover stays up through that. Window sanity is
			// measured against the data itself: a fitted window is within a
			// few multiples of the sessions' span, the unfitted default is
			// orders of magnitude wider.
			const saneWindowMs = Math.max(
				(spanEnd - spanStart + 2 * pad) * 4,
				30 * MINUTE_MS,
			);
			const timeline = this.timeline;
			let fitForced = false;
			let redrawForced = false;
			let stableFrames = 0;
			let lastSignature = null;
			const startedAt = performance.now();
			const checkRevealable = () => {
				if (this.renderGeneration !== generation || this.ready) {
					return;
				}
				if (!drawComplete) {
					// vis's initial-draw cycle is racy: usually next-frame,
					// sometimes parked until its 1s watch timer. One forced
					// redraw un-parks it.
					if (!redrawForced && performance.now() - startedAt > 120) {
						redrawForced = true;
						timeline.redraw();
					}
					requestAnimationFrame(checkRevealable);
					return;
				}
				const window = timeline.getWindow();
				const windowMs = window.end - window.start;
				if (windowMs <= saneWindowMs) {
					// The Range can be fitted while the DOM still shows the
					// old scrunched layout (vis defers that redraw, sometimes
					// to its 1s timer), so trust only PIXELS: the probe
					// item's rendered width must match what the fitted window
					// implies for its known duration. Until it does, force
					// redraws to pull the DOM forward.
					const probeEl =
						this.$refs.timelineEl?.querySelector(".vis-item.tl-probe");
					const probeRect = probeEl ? probeEl.getBoundingClientRect() : null;
					const expectedPx = probe
						? (probe.ms / windowMs) * this.$refs.timelineEl.offsetWidth
						: null;
					const pixelsConsistent =
						!probe ||
						(probeRect &&
							expectedPx > 0 &&
							probeRect.width > expectedPx * 0.3 &&
							probeRect.width < expectedPx * 3);
					if (!pixelsConsistent) {
						stableFrames = 0;
						timeline.redraw();
						requestAnimationFrame(checkRevealable);
						return;
					}
					const x = probeRect ? Math.round(probeRect.x) : null;
					const signature = `${window.start.getTime()}:${window.end.getTime()}:${x}`;
					stableFrames = signature === lastSignature ? stableFrames + 1 : 0;
					lastSignature = signature;
					if (stableFrames >= 2) {
						this.ready = true;
						return;
					}
				} else if (!fitForced) {
					// The fit is pending on vis's next cycle; run it now
					// rather than waiting for that cycle to come around.
					fitForced = true;
					timeline.fit({ animation: false });
				}
				requestAnimationFrame(checkRevealable);
			};
			requestAnimationFrame(checkRevealable);
			// Backstop: never leave the cover up forever if fit or the draw
			// callback cannot complete (pathological layouts).
			setTimeout(() => {
				if (this.renderGeneration === generation && !this.ready) {
					this.ready = true;
				}
			}, 3000);
		},
	},
};
</script>

<style scoped>
.timeline-wrap {
	position: relative;
	min-height: 120px;
}

/* While fetching, hold an area close to a typical chart height so the
   spinner is unmissable and the resize to the real chart height (which
   happens while the spinner still covers the area) stays small. */
.timeline-wrap-loading {
	min-height: 170px;
}

/* OPAQUE cover over the chart area until vis has both unhidden its root
   and applied the fitted window: between those two stages the chart is
   visible but zoomed to a days-wide default, and nothing mid-layout may
   ever be seen. */
.timeline-spinner {
	position: absolute;
	inset: 0;
	z-index: 1;
	background-color: rgb(var(--v-theme-surface));
}

.timeline-container {
	width: 100%;
}

.timeline-swatch {
	display: inline-block;
	width: 14px;
	height: 10px;
	border-radius: 2px;
}

/* Ribbon-shaped legend swatches: thin, like the marks they explain. */
.timeline-swatch-ribbon {
	height: 4px;
}

.swatch-text-work {
	background-color: var(--tl-kind-text);
}

.swatch-graph-work {
	background-color: var(--tl-kind-graph);
}

.swatch-table-work {
	background-color: var(--tl-kind-table);
}

.swatch-active {
	background-color: rgb(var(--v-theme-secondary));
}

.swatch-input-only {
	background: repeating-linear-gradient(
		45deg,
		rgb(var(--v-theme-warning)) 0 2px,
		transparent 2px 5px
	);
	border: 1px solid rgb(var(--v-theme-warning));
}

.swatch-orientation {
	background-color: var(--tl-kind-orientation);
}

.swatch-span {
	background-color: rgba(var(--v-theme-on-surface), 0.04);
	border: 1px solid rgba(var(--v-theme-on-surface), 0.3);
}

/* The minute bars: thin marks with the theme's work color; the surface gap
   between separate runs is real time, so no artificial spacer is needed. */
.timeline-container :deep(.vis-item.tl-active) {
	background-color: rgb(var(--v-theme-secondary));
	border-color: rgb(var(--v-theme-secondary-edge));
	height: 16px;
}

/* Rows are label-height (two lines) while the minute bars are 16px; vis
   anchors items near the row top by its own inline math, so the bars are
   re-centered explicitly. Safe because stack:false means one lane per
   row - every bar shares the same vertical home. */
.timeline-container :deep(.vis-item.tl-active),
.timeline-container :deep(.vis-item.tl-input-only) {
	/* Not translateY: vis positions items with an inline transform of its
	   own (horizontal), which would override ours. Half the 16px bar
	   height as a negative margin centers without touching transform. */
	top: 50% !important;
	margin-top: -8px;
}

/* The work-kind ribbons sit just below the centered 16px bar (which spans
   -8px..+8px around the row center): a 5px strip at +10px..+15px. */
.timeline-container :deep(.vis-item.tl-ribbon) {
	top: 50% !important;
	margin-top: 10px;
	height: 5px;
	border: none;
	border-radius: 2px;
}

.timeline-container :deep(.vis-item.tl-ribbon-orientation) {
	background-color: var(--tl-kind-orientation);
}

.timeline-container :deep(.vis-item.tl-ribbon-text) {
	background-color: var(--tl-kind-text);
}

.timeline-container :deep(.vis-item.tl-ribbon-graph) {
	background-color: var(--tl-kind-graph);
}

.timeline-container :deep(.vis-item.tl-ribbon-table) {
	background-color: var(--tl-kind-table);
}

/* Input-only wears the stripe texture of not-quite-work in the warning
   color, so solid = worked and amber stripes = input only stay readable
   at a glance against the plain span background. */
.timeline-container :deep(.vis-item.tl-input-only) {
	background: repeating-linear-gradient(
		45deg,
		rgb(var(--v-theme-warning)) 0 2px,
		transparent 2px 5px
	);
	border-color: rgb(var(--v-theme-warning));
	height: 16px;
}

/* The one background layer behind the minute bars: the outlined
   near-white span shows the wall-clock extent. */
.timeline-container :deep(.vis-item.vis-background.tl-span) {
	background-color: rgba(var(--v-theme-on-surface), 0.04);
	border-inline: 1px solid rgba(var(--v-theme-on-surface), 0.3);
}

.timeline-container :deep(.vis-labelset .vis-label) {
	color: rgba(var(--v-theme-on-surface), 0.87);
}

.timeline-container :deep(.tl-group-title) {
	font-size: 0.8rem;
	font-weight: 600;
	line-height: 1.2;
}

.timeline-container :deep(.tl-group-sub) {
	font-size: 0.7rem;
	color: rgba(var(--v-theme-on-surface), 0.6);
	line-height: 1.2;
}
</style>
